import { ExternalLink, Wrench } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useApi, PageHeader, Empty, Loading } from "../components/kit";
import { useBlobUrl } from "../components/Lesson";

function Uploaded({ fid, title }) {
  const url = useBlobUrl(fid);
  return url ? <img src={url} alt={title} className="h-14 w-14 rounded-xl object-cover" data-testid="link-image" /> : <div className="h-14 w-14 animate-pulse rounded-xl bg-muted" />;
}

export function LinkPic({ l }) {
  if (l.image_file_id) return <Uploaded fid={l.image_file_id} title={l.title} />;
  let host = "";
  try { host = new URL(l.url).hostname; } catch { host = ""; }
  return (
    <span className="grid h-14 w-14 place-items-center rounded-xl border bg-white">
      {host ? <img src={`https://www.google.com/s2/favicons?domain=${host}&sz=64`} alt="" className="h-8 w-8" data-testid="link-favicon" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        : <ExternalLink className="h-5 w-5 text-[color:var(--dcbc-red)]" aria-hidden="true" />}
    </span>
  );
}

function Card({ l, admin }) {
  if (l.url) return (
    <a href={l.url} target="_blank" rel="noreferrer" data-testid={`link-card-${l.id}`}
      className="group flex min-h-[110px] flex-col justify-between rounded-2xl border bg-white p-5 hover:border-[color:var(--ink)] transition-colors">
      <span className="flex items-start justify-between"><LinkPic l={l} /><ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-[color:var(--dcbc-red)]" aria-hidden="true" /></span>
      <div className="mt-4"><p className="font-display text-xl font-semibold">{l.title}</p>{l.description && <p className="text-sm text-muted-foreground">{l.description}</p>}</div>
    </a>
  );
  return admin ? (
    <Link to="/admin/settings" data-testid={`link-setup-${l.id}`}
      className="flex min-h-[110px] flex-col justify-between rounded-2xl border-2 border-dashed border-amber-400 bg-amber-50 p-5">
      <Wrench className="h-5 w-5 text-amber-700" aria-hidden="true" />
      <div><p className="font-display text-xl font-semibold">{l.title}</p><p className="text-sm text-amber-800">Setup task: add the URL. Hidden from employees until then.</p></div>
    </Link>
  ) : null;
}

export default function Links() {
  const { user } = useAuth();
  const { data, loading } = useApi("/links");
  const secs = useApi("/link-sections");
  if (loading || !secs.data) return <Loading />;
  const links = (data || []).filter((l) => l.url || user.is_superadmin);
  const ids = new Set(secs.data.map((s) => s.id));
  const groups = secs.data.map((s, k) => ({ ...s, links: links.filter((l) => l.section_id === s.id || (k === 0 && !ids.has(l.section_id))) })).filter((g) => g.links.length);
  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Shortcuts" title="Useful Links" testid="links-header">
        {user.is_superadmin && <Link to="/admin/settings" className="text-sm font-semibold text-[color:var(--dcbc-red)]" data-testid="links-organize">Organize links</Link>}
      </PageHeader>
      {groups.length === 0 && <Empty testid="links-empty">No links have been set up yet.</Empty>}
      {groups.map((g) => (
        <section key={g.id} className="space-y-3" data-testid={`links-section-${g.id}`}>
          <h2 className="eyebrow text-[color:var(--dcbc-red)]">{g.name}</h2>
          <div className="grid gap-4 sm:grid-cols-2">{g.links.map((l) => <Card key={l.id} l={l} admin={user.is_superadmin} />)}</div>
        </section>))}
    </div>
  );
}
